import * as S from './GlobalModal.styled';
import { Suspense, useEffect } from 'react';
import { PageLoader } from '@core-web/ui';
import { IGlobalModal } from './IGlobalModal';
import { addScrollbarClasses } from '@core-web/lib';
import { GlobalModalInnerContextProvider, useGlobalModalOuterContext, useGlobalModalInnerContext } from './context';

export const BaseGlobalModal = (props: IGlobalModal): JSX.Element => {
  // Children that is passed from the Global Model
  const { children } = props;

  // Global Modal State context from pageWrapper
  const { resetGlobalModalOuterProps } = useGlobalModalOuterContext();

  // Global Modal Inner State
  const {
    globalModalInnerConfig: {
      hideFooter,
      confirmButtonOnly,
      confirmModalButtonText,
      cancelButtonOnly,
      cancelButtonText,
    },
    modalTitle,
    activeScreen,
    resetActiveScreen,
    updateActiveScreen,
    onClose,
    onConfirm,
    headerActionButton,
    skipResetModal,
  } = useGlobalModalInnerContext();

  const isFirstScreen = activeScreen === 0;
  const modalTitleClass = !isFirstScreen ? 'alignCenter' : '';

  // Call Back Handlers
  const handleBackButtonClick = () => {
    updateActiveScreen(activeScreen - 1);
  };

  const resetModal = () => {
    resetActiveScreen();
    resetGlobalModalOuterProps();
  };

  const handleConfirmButtonClick = () => {
    onConfirm?.();
    if (!skipResetModal) {
      resetModal();
    }
  };

  const handleCancelButtonClick = () => {
    onClose?.();
    if (!skipResetModal) {
      resetModal();
    }
  };

  const ModalBodyClassName = 'globalModalBody';

  useEffect(() => {
    addScrollbarClasses(`.${ModalBodyClassName}`);
  }, []);

  return (
    <S.ModalContentWrapper>
      <S.ModalHeaderWrapper>
        <S.ModalHeaderContent>
          {!isFirstScreen && (
            <S.ModalBackWrapper onClick={handleBackButtonClick}>
              <S.ModalBackIcon />
              <S.ModalBackText>Back</S.ModalBackText>
            </S.ModalBackWrapper>
          )}
          <S.ModalTitle className={modalTitleClass}>{modalTitle}</S.ModalTitle>

          {!!headerActionButton && <S.ModalHeaderButton>{headerActionButton}</S.ModalHeaderButton>}
        </S.ModalHeaderContent>
      </S.ModalHeaderWrapper>
      <S.ModalBody className={ModalBodyClassName}>{children}</S.ModalBody>
      {!hideFooter && (
        <S.ModalFooterWrapper>
          {confirmButtonOnly ? (
            <S.ModalActionButton onClick={handleConfirmButtonClick}>
              {confirmModalButtonText || 'Save'}
            </S.ModalActionButton>
          ) : cancelButtonOnly ? (
            <S.ModalActionButton onClick={handleCancelButtonClick}>{cancelButtonText || 'Cancel'}</S.ModalActionButton>
          ) : (
            <>
              <S.ModalActionButton onClick={handleConfirmButtonClick}>
                {confirmModalButtonText || 'Save'}
              </S.ModalActionButton>
              <S.ModalActionButton onClick={handleCancelButtonClick}>
                {cancelButtonText || 'Cancel'}
              </S.ModalActionButton>
            </>
          )}
        </S.ModalFooterWrapper>
      )}
    </S.ModalContentWrapper>
  );
};

export const GlobalModal = (): JSX.Element => {
  // Global Modal State context from pageWrapper
  const { globalModalOuterProps } = useGlobalModalOuterContext();
  if (!globalModalOuterProps.isOpen) return <></>;
  return (
    <>
      <S.ModalOverlay></S.ModalOverlay>
      <GlobalModalInnerContextProvider>
        <S.ModalContainerWrapper size={globalModalOuterProps.size}>
          <Suspense fallback={<PageLoader isFullHeight={false} />}>
            <BaseGlobalModal>{globalModalOuterProps.children}</BaseGlobalModal>
          </Suspense>
        </S.ModalContainerWrapper>
      </GlobalModalInnerContextProvider>
    </>
  );
};

export default GlobalModal;
